// 감사 로그 행위 유형 — **표기와 분류를 한 곳에서** 정한다.
//
// ⚠️ 왜 공용으로 옮겼나: 예전에는 표가 두 벌이었다.
//      운영자 콘솔  AdminAuditPage.tsx 의 ACTION_LABELS
//      사용자 콘솔  api/tenantAudit.ts 의 AUDIT_LABELS (10종만)
//    한쪽에만 추가하면 다른 쪽에서 **원시 코드가 그대로 화면에 나온다.** 실제로 그랬다 —
//    ADMIN_ACCOUNT_VIEWED · LINK_CREATED · ENTITLEMENT_CHANGED 같은 영어가 보였고,
//    운영자 화면의 행위 분포에서는 20자가 넘어 막대 위로 넘치기까지 했다.
//    표가 하나면 그 사고가 구조적으로 없어진다.
//
// ⚠️ 정본은 서버의 kr.taplink.audit.AuditAction 이다(35종). 여기에 없는 코드가 오면
//    화면은 코드를 그대로 보여준다 — 감추면 "무슨 일이 있었는지"를 잃는다.
//    서버에 행위를 추가하면 **이 파일도 같이** 고쳐야 한다.

/** 화면에 보이는 이름. 없는 코드는 호출부에서 코드 자체를 보여준다. */
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  // 계정·인증
  SIGNUP: "가입",
  SOCIAL_SIGNUP: "소셜 가입",
  LOGIN_SUCCESS: "로그인",
  LOGIN_FAILURE: "로그인 실패",
  LOGIN_BLOCKED: "로그인 차단",
  LOGOUT: "로그아웃",
  TERMS_AGREE: "약관 동의",
  TERMS_WITHDRAW: "동의 철회",
  ACCOUNT_WITHDRAW: "회원 탈퇴",
  PASSWORD_RESET: "비밀번호 재설정",
  EMAIL_VERIFIED: "이메일 인증",
  ACCOUNT_DORMANT: "휴면 전환",

  // 조직
  TENANT_INVITED: "멤버 초대",
  TENANT_JOINED: "멤버 합류",
  TENANT_ROLE_CHANGED: "역할 변경",
  TENANT_MEMBER_REMOVED: "멤버 제거",

  // 링크
  LINK_CREATED: "링크 생성",
  LINK_UPDATED: "링크 수정",
  LINK_DEACTIVATED: "링크 비활성화",

  // 요금제·한도
  TENANT_PLAN_CHANGED: "조직 요금제 변경",
  ENTITLEMENT_CHANGED: "한도·기능 변경",
  PLAN_CHANGED: "요금제 정보 변경",
  PLAN_CREATED: "요금제 신설",

  // 메일
  MAIL_SUPPRESSION_RELEASED: "수신거부 해제",
  MAIL_TEST_SENT: "시험 메일 발송",

  // 운영자 조치
  ADMIN_ACCOUNT_VIEWED: "계정 열람",
  ADMIN_ACCOUNT_UNLOCKED: "로그인 잠금 해제",
  ADMIN_ACCOUNT_SUSPENDED: "이용 정지",
  ADMIN_ACCOUNT_RESTORED: "정지 해제",
  ADMIN_ACCOUNT_REACTIVATED: "휴면 복귀",
  ADMIN_VERIFICATION_RESENT: "인증메일 재발송",
  ADMIN_ROLE_GRANTED: "운영 권한 부여",
  ADMIN_ROLE_REVOKED: "운영 권한 회수",
  ADMIN_TENANT_SUSPENDED: "조직 정지",
  ADMIN_TENANT_RESTORED: "조직 정지 해제",
};

/**
 * 선택 상자의 묶음.
 *
 * 35종을 한 줄로 늘어놓으면 찾을 수 없다 — 서버 enum 도 이미 주석으로 같은 묶음을
 * 표시하고 있으므로 그 구분을 그대로 화면으로 옮긴다.
 * ⚠️ 순서가 화면 순서다. '계정'을 앞에, '운영자 조치'를 뒤에 둔 것은 운영자가
 *    사고를 볼 때 로그인·가입부터 훑기 때문이다.
 */
export const AUDIT_ACTION_GROUPS: ReadonlyArray<{ label: string; actions: readonly string[] }> = [
  {
    label: "계정·인증",
    actions: [
      "SIGNUP", "SOCIAL_SIGNUP", "LOGIN_SUCCESS", "LOGIN_FAILURE", "LOGIN_BLOCKED",
      "LOGOUT", "TERMS_AGREE", "TERMS_WITHDRAW", "ACCOUNT_WITHDRAW",
      "PASSWORD_RESET", "EMAIL_VERIFIED", "ACCOUNT_DORMANT",
    ],
  },
  {
    label: "조직",
    actions: ["TENANT_INVITED", "TENANT_JOINED", "TENANT_ROLE_CHANGED", "TENANT_MEMBER_REMOVED"],
  },
  {
    label: "링크",
    actions: ["LINK_CREATED", "LINK_UPDATED", "LINK_DEACTIVATED"],
  },
  {
    label: "요금제·한도",
    actions: ["TENANT_PLAN_CHANGED", "ENTITLEMENT_CHANGED", "PLAN_CHANGED", "PLAN_CREATED"],
  },
  {
    label: "메일",
    actions: ["MAIL_SUPPRESSION_RELEASED", "MAIL_TEST_SENT"],
  },
  {
    label: "운영자 조치",
    actions: [
      "ADMIN_ACCOUNT_VIEWED", "ADMIN_ACCOUNT_UNLOCKED", "ADMIN_ACCOUNT_SUSPENDED",
      "ADMIN_ACCOUNT_RESTORED", "ADMIN_ACCOUNT_REACTIVATED", "ADMIN_VERIFICATION_RESENT",
      "ADMIN_ROLE_GRANTED", "ADMIN_ROLE_REVOKED", "ADMIN_TENANT_SUSPENDED", "ADMIN_TENANT_RESTORED",
    ],
  },
];

/** 몰리면 공격 신호 — 숫자에도 표시해 색만으로 전달하지 않는다 */
export const AUDIT_ALERTING = new Set(["LOGIN_FAILURE", "LOGIN_BLOCKED"]);

/** 화면 표기. 표에 없으면 코드를 그대로 — 감추지 않는다. */
export function auditActionLabel(action: string): string {
  return AUDIT_ACTION_LABELS[action] ?? action;
}

/**
 * 주어진 코드 목록을 묶음별로 정리한다.
 *
 * @param available 서버가 알려준 실제 코드 목록. 여기에 없는 것은 선택지에 넣지 않는다 —
 *   고를 수 있는데 결과가 항상 0건인 항목은 고장으로 읽힌다.
 *   ⚠️ 반대로 **묶음에 없는 코드도 빠뜨리지 않는다.** 서버에 새 행위가 생겼는데 이 파일을
 *      아직 안 고친 경우, 그 코드는 '기타' 로 모아 여전히 고를 수 있게 한다.
 */
export function groupAuditActions(
  available: readonly string[],
): Array<{ label: string; actions: string[] }> {
  const pool = new Set(available);
  const out: Array<{ label: string; actions: string[] }> = [];

  for (const group of AUDIT_ACTION_GROUPS) {
    const actions = group.actions.filter((a) => pool.has(a));
    actions.forEach((a) => pool.delete(a));
    if (actions.length) out.push({ label: group.label, actions });
  }
  // 어느 묶음에도 없던 것들 — 정렬해서 마지막에 붙인다
  if (pool.size) out.push({ label: "기타", actions: [...pool].sort() });
  return out;
}
